import {
  UseQueryOptions,
  useQueries,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import {
  ActivityValue,
  FlatActivityModel,
  ValidationStatus
} from 'types/shared'
import { AllowedLocales } from 'utils/date'

import {
  useValidateActivityValue,
  UseValidateActivityValueProps,
  ValidateFlatActivityValueProps
} from 'utils/validateActivityValue'
import { useValidateUsingCallback } from 'modules/interaction/utils/validateUsingCallback'

type ValidableActivity = Pick<
  ValidateFlatActivityValueProps,
  'activityModel' | 'value'
>

type ValidationResult = {
  validationStatus: ValidationStatus
  helperText?: {
    des: string
    complement?: string
  }
}

const useValidationErrorContext =
  (): UseValidateActivityValueProps['context'] => {
    const { language } = useTranslation()

    return useMemo(
      () => ({
        lang: language as AllowedLocales
      }),
      [language]
    )
  }

export const useImperativeValidateActivities = () => {
  const queryClient = useQueryClient()
  const context = useValidationErrorContext()
  const { validateFlatActivityValue } = useValidateActivityValue({ context })

  const validateActivities = useCallback(
    async (activities: ValidableActivity[]) =>
      Promise.all(
        activities.map(({ activityModel, value }) =>
          queryClient.fetchQuery({
            queryKey: ['validateFlatActivityValue', activityModel.uuid, value],
            queryFn: () =>
              validateFlatActivityValue({
                activityModel,
                value,
                skipCallback: true
              }),
            initialData: {
              validationStatus: 'pending' as ValidationStatus
            } as ValidationResult
          })
        )
      ),
    [queryClient, validateFlatActivityValue]
  )

  return validateActivities
}

type UseValidateActivitiesProps = {
  activities: ValidableActivity[]
  enabled?: boolean
  skipCallback?: boolean
}

export const useValidateActivities = ({
  activities,
  enabled,
  skipCallback
}: UseValidateActivitiesProps) => {
  const context = useValidationErrorContext()
  const { validateFlatActivityValue } = useValidateActivityValue({ context })

  const results =
    useQueries({
      queries: activities.map(
        ({ activityModel, value }) =>
          ({
            queryKey: [
              'validateFlatActivityValue',
              activityModel.uuid,
              value,
              skipCallback
            ],
            queryFn: () =>
              validateFlatActivityValue({ activityModel, value, skipCallback }),
            initialData: {
              validationStatus: 'pending' as ValidationStatus
            } as ValidationResult,
            enabled
          }) satisfies UseQueryOptions
      )
    }) || []

  type Result = (typeof results)[number]

  type ResultWithInitialData = Omit<Result, 'data'> & {
    data: NonNullable<Result['data']>
  }

  return results as ResultWithInitialData[]
}

type UseValidateActivityProps = {
  activityModel: FlatActivityModel
  value?: ActivityValue
  enabled?: boolean
}

export default function useValidateActivity({
  activityModel,
  value,
  enabled
}: UseValidateActivityProps) {
  const activities = useMemo(
    () => [{ activityModel, value }],
    [activityModel, value]
  )

  const [{ data, ...queryInfo }] = useValidateActivities({
    activities,
    enabled,
    skipCallback: true
  })

  return {
    ...data,
    ...queryInfo
  }
}

export const useValidateActivityCallback = ({
  activityModel,
  value,
  enabled
}: UseValidateActivityProps) => {
  const validateUsingCallback = useValidateUsingCallback()
  const [result, setResult] = useState<ValidationResult | null>(null)
  const [isFetching, setIsFetching] = useState(false)

  const hasCallback = Boolean(activityModel.validation_activity_callback)

  useEffect(() => {
    setResult(null)
  }, [value])

  const triggerValidation = useCallback(async () => {
    if (!enabled || !hasCallback) return

    setIsFetching(true)
    try {
      const callbackResult = await validateUsingCallback({
        validation_activity_callback:
          activityModel.validation_activity_callback,
        value
      })
      setResult(callbackResult)
    } finally {
      setIsFetching(false)
    }
  }, [
    enabled,
    hasCallback,
    activityModel.validation_activity_callback,
    value,
    validateUsingCallback
  ])

  return {
    validationStatus: result?.validationStatus,
    helperText: result?.helperText,
    isFetching,
    triggerValidation
  }
}
